import { createSlice } from '@reduxjs/toolkit'

/**
 * The shape a blank user form starts from.
 *
 * Extracted into a constant because the reset reducer needs it. It used to
 * blank every field by assigning the empty STRING to each key, which is fine
 * for text inputs and wrong for anything else — see resetUserCreateUpdateFormValue.
 */
const emptyForm = () => ({
    name: "",
    email: "",
    password: "",
    package_id: "",
    package_level_starting_id: "",
    status: "active",
    // Which shops this user works in, and the role they hold in EACH:
    //   [{ shop_id, role_id }]
    //
    // Replaces the single flat `role_id`. A role is a property of the
    // (user, shop) pair now — the same person can be a Manager at one branch
    // and a Cashier at another, and one field cannot express that.
    shop_roles: [],
})

export const UserManagementSlice = createSlice({
    name: 'userManagement',
    initialState: {
        blogPost: [],
        formValue: emptyForm(),
        userList: [],
        userLoginHistoryList: [],
    },
    reducers: {

        onChangeUserCreateUpdateFormInput: (state, action) => {
            state.formValue[`${action.payload.name}`] = action.payload.value;
        },

        /**
         * ⚠️ Rebuilt from the empty shape rather than blanking each key.
         *
         * This used to be:
         *
         *     Object.keys(formValue).forEach((i) => formValue[i] = "")
         *
         * which sets EVERY field to the empty string. That is harmless for text
         * inputs and breaks `shop_roles`, which has to stay an array: the form
         * maps over it on the very next render, and "" has no .map, so opening
         * the create screen would throw. It also silently dropped `status`,
         * whose default is "active" rather than blank.
         */
        resetUserCreateUpdateFormValue: (state) => {
            state.formValue = emptyForm();
        },

        /** Add an empty shop/role row for the admin to fill in. */
        addShopRole: (state) => {
            state.formValue.shop_roles.push({ shop_id: "", role_id: "" });
        },

        removeShopRole: (state, action) => {
            state.formValue.shop_roles.splice(action.payload, 1);
        },

        /**
         * Set one field of one row.
         *
         * A dedicated reducer rather than rebuilding the array in the component
         * on every keystroke, which is how the array identity churns and the
         * rows lose focus mid-selection.
         */
        setShopRole: (state, action) => {
            const { index, field, value } = action.payload;
            if (state.formValue.shop_roles[index]) {
                state.formValue.shop_roles[index][field] = value;
            }
        },

        /** Replace the whole set — used when loading an existing user. */
        setShopRoles: (state, action) => {
            state.formValue.shop_roles = Array.isArray(action.payload) ? action.payload : [];
        },

        setUserList: (state, action) => {
            state.userList = action.payload
        },
        setUserLoginHistoryList: (state, action) => {
            state.userLoginHistoryList = action.payload
        },
    },
})

export const {
    onChangeUserCreateUpdateFormInput,
    resetUserCreateUpdateFormValue,
    addShopRole,
    removeShopRole,
    setShopRole,
    setShopRoles,
    setUserList,
    setUserLoginHistoryList,
} = UserManagementSlice.actions

export default UserManagementSlice.reducer
